import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { ApiResponse } from '../shared/types';

/**
 * CWS-1776: the company's switch for cancel & refund (CWS-1534) and the
 * per-location availability the shop UI gates the Cancel / Refund buttons on.
 *
 * The switch is a platform decision made from the super-admin company page;
 * the shop side only ever reads availability.
 */
export interface CancellationSettings {
  enabled: boolean;
  /** Empty = every location. */
  location_ids: string[];
  /** Whether the Prorated refund option is offered at all for this company. */
  prorated_refund_enabled: boolean;
  updated_by: string | null;
  updated_at: string | null;
}

export interface CancellationLocation {
  id: string;
  branch: string | null;
  timezone: string;
}

export interface CancellationSettingsData {
  settings: CancellationSettings;
  locations: CancellationLocation[];
  /** The platform kill switch (env). Off means the company toggle has no effect yet. */
  globally_enabled: boolean;
}

export interface CancellationSettingsUpdatePayload {
  enabled: boolean;
  location_ids?: string[];
  /** Omitted keeps the stored value. */
  prorated_refund_enabled?: boolean;
}

export type CancellationAvailabilityReason = 'ok' | 'platform_disabled' | 'location_disabled';

export interface CancellationAvailability {
  location_id: string;
  enabled: boolean;
  reason: CancellationAvailabilityReason;
  prorated_refund_enabled: boolean;
}

/** What the settings panel needs from whoever hosts it (the super-admin company page). */
export interface CancellationSettingsSource {
  getSettings(): Observable<ApiResponse<CancellationSettingsData>>;
  updateSettings(
    payload: CancellationSettingsUpdatePayload
  ): Observable<ApiResponse<CancellationSettingsData>>;
}

@Injectable({ providedIn: 'root' })
export class CancellationSettingsService {
  constructor(private api: ApiService) {}

  /** Shop side: is cancel & refund on at this location? */
  getAvailability(locationId: string): Observable<ApiResponse<CancellationAvailability>> {
    return this.api.get<CancellationAvailability>('order/cancellations/availability', {
      location_id: locationId,
    });
  }
}
